import {createContext, type Dispatch, type ReactNode, useContext, useReducer} from "react";

export interface QuizFormData {
    [key: string]: any;
}

export interface QuizState {
    currentStep: number;
    formData: QuizFormData;
    isSubmitting: boolean;
    error?: string;
}

const initialState: QuizState = {
    currentStep: 0,
    formData: {},
    isSubmitting: false,
    error: undefined
};

export const QuizActionTypes = {
    NEXT_STEP: 'NEXT_STEP',
    PREV_STEP: 'PREV_STEP',
    GO_TO_STEP: 'GO_TO_STEP',
    UPDATE_FORM_DATA: 'UPDATE_FORM_DATA',
    SET_SUBMITTING: 'SET_SUBMITTING',
    SET_ERROR: 'SET_ERROR',
    RESET_QUIZ: 'RESET_QUIZ'
} as const;

export type QuizAction =
    | { type: typeof QuizActionTypes.NEXT_STEP }
    | { type: typeof QuizActionTypes.PREV_STEP }
    | { type: typeof QuizActionTypes.GO_TO_STEP; payload: number }
    | { type: typeof QuizActionTypes.UPDATE_FORM_DATA; payload: Partial<QuizFormData> }
    | { type: typeof QuizActionTypes.SET_SUBMITTING; payload: boolean }
    | { type: typeof QuizActionTypes.SET_ERROR; payload: string | undefined }
    | { type: typeof QuizActionTypes.RESET_QUIZ };

function quizReducer(state: QuizState, action: QuizAction): QuizState {
    switch (action.type) {
        case QuizActionTypes.NEXT_STEP:
            return {
                ...state,
                currentStep: state.currentStep + 1
            };
        case QuizActionTypes.PREV_STEP:
            return {
                ...state,
                currentStep: Math.max(0, state.currentStep - 1)
            };
        case QuizActionTypes.GO_TO_STEP:
            return {
                ...state,
                currentStep: action.payload
            };
        case QuizActionTypes.UPDATE_FORM_DATA:
            return {
                ...state,
                formData: {
                    ...state.formData,
                    ...action.payload
                }
            };
        case QuizActionTypes.SET_SUBMITTING:
            return {
                ...state,
                isSubmitting: action.payload
            };
        case QuizActionTypes.SET_ERROR:
            return {
                ...state,
                error: action.payload
            };
        case QuizActionTypes.RESET_QUIZ:
            return initialState;
        default:
            return state;
    }
}

const QuizStateContext = createContext<QuizState | undefined>(undefined);
const QuizDispatchContext = createContext<Dispatch<QuizAction> | undefined>(undefined);

export function QuizProvider({children}: { children: ReactNode }): JSX.Element {
    const [state, dispatch] = useReducer(quizReducer, initialState);
    return (
        <QuizStateContext.Provider value={state}>
            <QuizDispatchContext.Provider value={dispatch}>
                {children}
            </QuizDispatchContext.Provider>
        </QuizStateContext.Provider>
    );
}

export function useQuiz(): {
    state: QuizState;
    dispatch: Dispatch<QuizAction>;
} {
    const state = useContext(QuizStateContext);
    const dispatch = useContext(QuizDispatchContext);

    if (state === undefined || dispatch === undefined) {
        throw new Error('useQuiz must be used within a QuizProvider');
    }

    return {state, dispatch};
}

export const quizActions = {
    nextStep: (): QuizAction => ({
        type: QuizActionTypes.NEXT_STEP
    }),

    prevStep: (): QuizAction => ({
        type: QuizActionTypes.PREV_STEP
    }),

    goToStep: (step: number): QuizAction => ({
        type: QuizActionTypes.GO_TO_STEP,
        payload: step
    }),

    updateFormData: (data: Partial<QuizFormData>): QuizAction => ({
        type: QuizActionTypes.UPDATE_FORM_DATA,
        payload: data
    }),

    setSubmitting: (isSubmitting: boolean): QuizAction => ({
        type: QuizActionTypes.SET_SUBMITTING,
        payload: isSubmitting
    }),

    setError: (error?: string): QuizAction => ({
        type: QuizActionTypes.SET_ERROR,
        payload: error
    }),

    resetQuiz: (): QuizAction => ({
        type: QuizActionTypes.RESET_QUIZ
    })
};